<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import { computed, ref, useCssModule, watch } from 'vue';

  import { UI_TIMEOUT } from '../../constants';
  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';
  import IconLabel from '../IconLabel/IconLabel.vue';
  import Tooltip from '../Tooltip/Tooltip.vue';

  export interface CopyProps {
    /**
     * Optional text to display instead of icon.
     */
    text?: string;

    /**
     * The value to copy to clipboard.
     */
    value: string;

    /**
     * Sets the copy icon or text to always be visible. By default, icon/text only appears when hovering over slot content.
     */
    visible?: boolean;

    /**
     * Sets the mode of the copy component.
     */
    // TODO: remove unset mode eventually but it will cause a breaking change
    mode?: 'icon' | 'text' | 'icon-button' | 'unset';
  }

  const props = withDefaults(defineProps<CopyProps>(), {
    text: undefined,
    visible: false,
    mode: 'unset',
  });

  /**
   * Compute the mode based on the props for backwards compatibility.  Previously the checks were done
   * in the template based on text being present.  Have to calculate it here after the text prop is set.
   */
  const computedMode = computed(() => {
    // If the mode is set, use it (new behavior)
    if (props.mode !== 'unset') return props.mode;

    // Otherwise, determine it based on the text prop (old behavior)
    return props.text ? 'text' : 'icon';
  });

  // Watch the props individually to ensure we run validation when props change.
  watch(
    [computedMode, () => props.text, () => props.visible],
    () => {
      // Validate the mode based on the text prop.  This will still pass if the mode is unset but the text prop is present.
      if (!props.text && ['text', 'icon-button'].includes(computedMode.value)) {
        throw new Error('Copy: text prop is required when mode is text or icon-button');
      } else if (props.text && computedMode.value === 'icon') {
        throw new Error('Copy: text prop is not allowed when mode is icon');
      }

      // If the mode is icon-button and the visible prop is false,
      // warn the user that the visible prop is ignored when mode is icon-button
      // Because we have to show the whole control
      if (computedMode.value === 'icon-button' && props.visible === false) {
        logger.warn('Copy: visible prop is ignored when mode is icon-button');
      }
    },
    { immediate: true },
  );

  const emit = defineEmits<{
    /**
     * Emitted when the copy action is completed.
     */
    (e: 'copy'): void;
  }>();

  const classes = useCssModule();

  // Flag to indicate whether a user successfully copied the text to their clipboard.
  // Note: We reset this flag after a timeout to allow the user to copy the text multiple times.
  const isCopied = ref(false);

  const tooltip = computed(() => {
    return isCopied.value ? t('ll.copy.copied') : !props.text ? t('ll.copy.copyToClipboard') : null;
  });

  function handleCopy() {
    navigator.clipboard.writeText(props.value);
    isCopied.value = true;

    // Reset the copied state after the timeout to flip the icon back
    setTimeout(() => {
      isCopied.value = false;
    }, UI_TIMEOUT);

    emit('copy');
  }
</script>

<template>
  <div class="stash-copy flex items-center" :class="{ [classes.hidden]: !props.visible }" data-test="stash-copy">
    <!-- If the mode is not icon-button, render the appropriate content based on the mode -->
    <template v-if="computedMode !== 'icon-button'">
      <!-- @slot Anything you want to render. This is not what will be copied to the clipboard. -->
      <slot></slot>
      <Tooltip
        side="top"
        :text="tooltip"
        :is-disabled="!!props.text"
        class="stash-copy__copy-zone ml-3 cursor-pointer"
        :class="classes.copy"
        data-test="stash-copy|copy-zone"
      >
        <!-- Regardless of the mode, we always render ONLY the success icon if the text has been copied 
         Which is why this is the first in the `v-if` block-->
        <Icon
          v-if="isCopied"
          class="stash-copy__success-icon text-green-500"
          name="circle-check"
          data-test="stash-copy|success-icon"
        />
        <!-- If the mode is text, we render a button with the text -->
        <Button
          v-else-if="computedMode === 'text'"
          class="stash-copy__copy-text"
          inline
          data-test="stash-copy|copy-target"
          @click="handleCopy"
        >
          {{ props.text }}
        </Button>
        <!-- If the mode is icon, we render just the icon -->
        <Icon
          v-else
          class="stash-copy__copy-icon"
          name="copy"
          title="Copy to clipboard"
          data-test="stash-copy|copy-target"
          @click="handleCopy"
        />
      </Tooltip>
    </template>
    <template v-else>
      <!-- If the mode is icon-button, we render an icon button with the icon and text -->
      <Button
        icon-label
        :data-test="isCopied ? 'stash-copy|success-icon-button' : 'stash-copy|copy-target'"
        :title="props.text"
        :class="isCopied ? 'text-green-500' : ''"
        @click="handleCopy"
      >
        <IconLabel :icon="isCopied ? 'circle-check' : 'copy'" :title="props.text" stacked>
          {{ props.text }}
        </IconLabel>
      </Button>
    </template>
  </div>
</template>

<style module>
  .hidden {
    .copy {
      visibility: hidden;
    }

    &:hover {
      .copy {
        visibility: visible;
      }
    }
  }
</style>
